import React, { FC, useEffect, useState } from "react"
import { DEF_PG_SIZE_SUB } from "../../constants/maximum-page-size"
import { useLocale } from "../../contexts/localeContext"
import { TableContextProvider } from "../../contexts/table/TableContextProvider"
import { TranslationKeys } from "../../contexts/TranslationKeys"
import presetActionColumnsDescriptor from "../../models/ColumnDefinitions/PresetActionColumn"
import { ListData } from "../../models/ListData"
import { ListQuery } from "../../models/ListQuery"
import { Lookup } from "../../models/Lookup"
import LookupTypes from "../../models/LookupTypes"
import presetActionSearchDescriptor from "../../models/search/PresetActionSearch"
import SiteSearch from "../../models/search/SiteSearch"
import { getPresetActions } from "../../services/presetActionService"
import { savePreset } from "../../services/presetService"
import { getSiteNamesWithPagination } from "../../services/siteService"
import { getErrorMessage } from "../../services/utilities/ServiceError"
import { useDialog } from "../dialog/DialogContext"
import { SelectType } from "../settingFields/SettingSelectField"
import TableTab from "../tabs/table-tab"
import { toast } from "react-toastify";
import { PresetSettingsWizard, SystemProfileTypeIds } from "./presetSettingsWizard"
import { getDeviceSettingsTableLegendItems } from "../../models/TableLegend"

interface Props {
    itemId: string,
    onDelete: any,
    onSave: (createNew) => void;
    profileId?: any;
    currentItemDetails?: any;
    reportTitle?: string
}

const PresetActions: FC<Props> = ({ itemId, onDelete, onSave, profileId = SystemProfileTypeIds.Reader, children, currentItemDetails, reportTitle}) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.assignedActions);
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [presetActionId, setPresetActionId] = useState('');
    const dialog = useDialog();

    useEffect(() => {
        (async () => {

            setLookup([{
                name: LookupTypes.site,
                values: [],
                dataMethod: getSiteNamesWithPagination,
                searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
            }]);
        })();
    }, []);

    const handleSave = (formFields, createNew) => {
        savePreset(formFields, createNew)
            .then(success => {
                toast.success(translate.byKey(TranslationKeys.readerPresetSaved));
                onSave(createNew);
                dataRefresh((query) => getPresetActions(itemId, translate, query));
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) })
            });
        setPresetActionId('');
    }

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    return <TableContextProvider
        dataMethod={(listParameters?: ListQuery) => getPresetActions(itemId, translate, listParameters)}
        manualDataRefresh={manualDataRefresh}
        tableDescriptor={presetActionColumnsDescriptor}
        searchDescriptor={presetActionSearchDescriptor}
        useTable
        usePagination
        useSearch
        useSort
        useFilter
        useLegend
        legendItems={getDeviceSettingsTableLegendItems(translate)}
        deleteMethod={onDelete}
        maxPageSize={DEF_PG_SIZE_SUB}
        editMethod={(presetInputId) => { setPresetActionId(presetInputId); setShowModal(true) }}
        lookup={lookup}>
        <TableTab
            title={title}
            selectRows={false}
            customReportDescription={`inputs for preset "${currentItemDetails?.name}" on site "${currentItemDetails?.site}"`}
            reportTitle={reportTitle}
            cyName="presetActions">
            <button type="button" className="flex px-4 bg-lime rounded-lg shadow text-white text-sm items-center"
                onClick={() => { setPresetActionId(''); setShowModal(true) }}>
                {translate.byKey(TranslationKeys.add)}
            </button>
            <PresetSettingsWizard
                isOpen={showModal}
                onClose={() => {
                    setPresetActionId('');
                    setShowModal(false);
                }}
                title={presetActionId ? translate.byKey(TranslationKeys.editActionPreset) : translate.byKey(TranslationKeys.newActionPreset)}
                presetId={presetActionId}
                systemProfileId={profileId}
                parentProfileId={itemId}
                presetTemplateType={SelectType.presetActionTemplate}
                onSave={handleSave}>
            </PresetSettingsWizard>
            {children}
        </TableTab>
    </TableContextProvider>
};

export default PresetActions;
